import React from 'react';
import Icon from '../../../components/AppIcon';

const ConfirmationSummary = ({ persona, formData, files }) => {
  const personaLabels = {
    'insurance-adjuster': 'Insurance Adjuster',
    'plaintiff-attorney': 'Plaintiff Attorney',
    'defense-attorney': 'Defense Attorney',
    'siu-investigator': 'SIU Investigator',
    'prosecutor': 'Prosecutor',
    'fire-marshal': 'Fire Marshal/AHJ',
    'risk-manager': 'Risk Manager',
    'property-owner': 'Property Owner/Insured',
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const imageFiles = files?.filter((f) => f?.type?.startsWith('image/'));
  const pdfFiles = files?.filter((f) => f?.type === 'application/pdf');

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="p-4 md:p-6 rounded-lg bg-error/10 border-2 border-error">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={24} className="text-error flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-lg md:text-xl text-error mb-2">
              One-Shot Submission Policy
            </h3>
            <p className="text-sm md:text-base text-foreground mb-3">
              This is a <strong>one-time submission</strong>. Once you submit this case, you cannot edit or add additional files. Any revisions or supplemental evidence will require creating a new case and will consume an additional case credit.
            </p>
            <p className="text-sm md:text-base text-foreground">
              Please review all information carefully before proceeding.
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-6">
        <div>
          <h3 className="text-lg md:text-xl font-semibold mb-4">Case Summary</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="p-4 rounded-lg bg-card border border-border">
              <p className="text-sm text-muted-foreground mb-1">Selected Persona</p>
              <p className="font-medium text-base md:text-lg">
                {personaLabels?.[persona] || 'Not selected'}
              </p>
            </div>

            <div className="p-4 rounded-lg bg-card border border-border">
              <p className="text-sm text-muted-foreground mb-1">Incident Date</p>
              <p className="font-medium text-base md:text-lg">
                {formatDate(formData?.incidentDate)}
              </p>
            </div>

            <div className="p-4 rounded-lg bg-card border border-border md:col-span-2">
              <p className="text-sm text-muted-foreground mb-1">Property Address</p>
              <p className="font-medium text-base md:text-lg">
                {formData?.propertyAddress || 'Not provided'}
              </p>
            </div>

            <div className="p-4 rounded-lg bg-card border border-border">
              <p className="text-sm text-muted-foreground mb-1">Property Type</p>
              <p className="font-medium text-base md:text-lg">
                {formData?.propertyType || 'Not provided'}
              </p>
            </div>

            <div className="p-4 rounded-lg bg-card border border-border">
              <p className="text-sm text-muted-foreground mb-1">Allegation Type</p>
              <p className="font-medium text-base md:text-lg">
                {formData?.allegationType || 'Not provided'}
              </p>
            </div>

            {formData?.estimatedLoss && (
              <div className="p-4 rounded-lg bg-card border border-border">
                <p className="text-sm text-muted-foreground mb-1">Estimated Loss</p>
                <p className="font-medium text-base md:text-lg">
                  ${parseFloat(formData?.estimatedLoss)?.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-lg md:text-xl font-semibold mb-4">Uploaded Evidence</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 md:p-6 rounded-lg bg-card border border-border">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Image" size={24} className="text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-base md:text-lg">{imageFiles?.length}</p>
                  <p className="text-sm text-muted-foreground">Images</p>
                </div>
              </div>
            </div>

            <div className="p-4 md:p-6 rounded-lg bg-card border border-border">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-secondary/10 flex items-center justify-center">
                  <Icon name="FileText" size={24} className="text-secondary" />
                </div>
                <div>
                  <p className="font-semibold text-base md:text-lg">{pdfFiles?.length}</p>
                  <p className="text-sm text-muted-foreground">PDF Documents</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {formData?.additionalContext && (
          <div>
            <h3 className="text-lg md:text-xl font-semibold mb-4">Additional Context</h3>
            <div className="p-4 rounded-lg bg-card border border-border">
              <p className="text-sm md:text-base text-foreground whitespace-pre-wrap">
                {formData?.additionalContext}
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="flex items-start gap-2 p-4 rounded-lg bg-primary/10 border border-primary/20">
        <Icon name="Clock" size={20} className="text-primary flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-primary mb-1">Processing Timeline</p>
          <p className="text-muted-foreground">
            After submission, our AI will analyze your evidence and may ask clarifying questions if confidence is not high. The preliminary report typically generates within 15-30 minutes after all questions are answered.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationSummary;